import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Text from "../Text";
import Img from "../Img";
import imgLeft from '../../assets/img/img_left.svg'; // or your uploaded room image
import imgRight from '../../assets/img/img_right.svg'; // or your uploaded room image
import { useTranslation } from "react-i18next";


interface Animal {
  // Define the shape of an animal object as needed
  id: string | number;
  name?: string;
  image?: string;
  [key: string]: any;
}

interface AnimalCarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  animals: Animal[];
}

function AnimalCarousel({ animals, ...props }: AnimalCarouselProps) {
  const { t, i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  
  const nextSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex + 1) % animals.length);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const prevSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex - 1 + animals.length) % animals.length);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  // Auto-scroll every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full flex flex-col items-center" {...props}>
      <Text
        as="h2"
        className="mt-12 text-[56px] font-bold font-upheavalttbrk uppercase tracking-widest text-center mb-8"
        style={{
          fontSize: '3em',
        }}
      >
        {t("home.carousel.animal.title")}
      </Text>
      
      <Text
        as="p"
        className="text-[28px] font-normal !text-white-a700 text-center max-w-5xl mb-16 px-8 font-upheaval animal-description"
        style={{
          fontSize: '1.5em', marginBottom: '100px'
        }}
      >
        {t("home.carousel.animal.subtitle_p1")}
        <span className="highlight">{t("home.carousel.animal.subtitle_p2")}</span>{t("home.carousel.animal.subtitle_p3")} <span className="highlight">{t("home.carousel.animal.subtitle_p4")}</span>
      </Text>
      
      <div className="relative w-full max-w-6xl flex flex-col justify-center items-center">
        {/* Carousel main container */}
        <div className="w-full flex items-center justify-center px-4 relative">
          {/* Left Arrow */}
          <button 
            onClick={prevSlide} 
            className="absolute left-0 z-30 p-2 cursor-pointer transform -translate-x-1/2"
            aria-label="Previous animal"
            style={{marginLeft: '24px'}}
          >
            <Img 
              src={imgLeft} 
              alt="Previous" 
              className="h-[52px] w-[30px] object-contain" 
            />
          </button>
          
          {/* Main card - always centered and prominent */}
          <div className="flex justify-center items-center gap-10 px-4 overflow-visible w-full relative" style={{gap: '2rem'}}>
            {[-1, 0, 1].map((offset) => {
              const index = (currentIndex + offset + animals.length) % animals.length;
              const isMain = offset === 0;
              
              // Different styling for main card vs side cards
              const cardClass = isMain 
                ? "z-20 transform scale-125 w-[320px]" 
                : offset < 0 
                  ? "z-10 opacity-60 -mr-16 w-[260px]" 
                  : "z-10 opacity-60 -ml-16 w-[260px]";
              
              return (
                <div 
                  key={index}
                  className={`transition-all duration-300 ${cardClass}`}
                >
                  <div className={`animal-card-container ${isMain ? 'main-card' : 'side-card'}`}>
                    <div className="pixel-animal-container">
                      <div className="pixel-animal-box">
                        <Img 
                          src={animals[index].image} 
                          alt={animals[index].name}
                          style={{ width: 'auto', height: '200px' }}
                          className="w-auto object-contain mb-3" 
                        />
                        <Text 
                          as="p" 
                          className="text-[28px] font-normal font-upheaval text-black text-center"
                          style={{ fontSize: '2em' }}
                      >
                          {animals[index].name}
                        </Text>
                        
                        {/* Animal stats */}
                        <div className="flex flex-col w-full mt-3 items-center gap-1">
                          <Text 
                            as="p" 
                            className="text-[18px] font-normal font-upheaval text-black text-center"
                          style={{ fontSize: '1.3em' }}
                         >
                            MILK POWER: {animals[index].milkRate}
                          </Text>
                          <Text 
                            as="p" 
                            className="text-[18px] font-normal font-upheaval text-black text-center"
                          style={{ fontSize: '1.3em' }}
                          >
                            PRICE: {animals[index].price}
                          </Text>
                          <Text 
                            as="p" 
                            className="text-[18px] font-normal font-upheaval text-black text-center"
                          style={{ fontSize: '1.3em' }}
                        >
                            FOOD NEEDED: {animals[index].energy}
                          </Text>
                        </div>
                      </div>
                      <div className="pixel-scanlines"></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Right Arrow */}
          <button 
            onClick={nextSlide} 
            className="absolute right-0 z-30 p-2 cursor-pointer transform translate-x-1/2"
            aria-label="Next animal"
          >
            <Img 
              src={imgRight} 
              alt="Next" 
              className="h-[52px] w-[30px] object-contain" 
            />
          </button>
        </div>
        
        {/* Navigation dots */}
        <div className="flex justify-center gap-3 mt-16">
          {animals.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                setIsAnimating(true);
                setTimeout(() => setIsAnimating(false), 300);
              }}
              className={`w-3 h-3 rounded-full cursor-pointer transition-all duration-300 ${
                index === currentIndex ? "bg-[#507c83]" : "bg-gray-600 opacity-40"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

AnimalCarousel.propTypes = {
  animals: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      rarity: PropTypes.string,
      milkRate: PropTypes.string,
      price: PropTypes.string,
      energy: PropTypes.string
    })
  ).isRequired
};

export { AnimalCarousel }; 